import {
    type FC,
    memo,
    useState,
    useCallback,
    useMemo,
    useRef,
    createContext,
    useContext,
    type ReactNode,
} from "react";
import ReactMarkdown, { type Components, type ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Element } from "hast";
import { cn } from "@/components/podkit/lib/cn";
import { InlineCommentAnnotation } from "@/routes/environments/code-changes/InlineCommentAnnotation";
import type { ReviewComment } from "@/components/ai/review/types";

/**
 * Tracks whether we're inside a CommentableBlock already.
 * Only the outermost block shows the "+" button and renders comments,
 * preventing duplicate buttons on nested markdown elements.
 */
const CommentableNestingContext = createContext(false);

/** Data attribute on the preview root; buttons use it to find the gutter column */
const PREVIEW_ROOT_ATTR = "data-md-preview-root";

/**
 * "+" button that always renders in the preview root's gutter column,
 * regardless of how deeply nested the block element is.
 *
 * Positioned absolutely within its relative parent (CommentableBlock).
 * On mount, measures the offset to the preview root's left edge and
 * sets `left` so every button lines up in the same column.
 */
const CommentGutterButton: FC<{ onPress: () => void; startLine: number }> = ({ onPress, startLine }) => {
    const refCallback = useCallback((el: HTMLButtonElement | null) => {
        if (!el) return;
        const root = el.closest<HTMLElement>(`[${PREVIEW_ROOT_ATTR}]`);
        if (!root || !el.offsetParent) return;
        const rootLeft = root.getBoundingClientRect().left;
        const parentLeft = el.offsetParent.getBoundingClientRect().left;
        el.style.left = `${rootLeft - parentLeft}px`;
    }, []);

    return (
        <button
            ref={refCallback}
            type="button"
            onClick={onPress}
            data-tracking-id="markdown-preview-add-comment"
            className="absolute top-0.5 flex size-5 items-center justify-center rounded-md bg-surface-primary-inverted font-mono text-base text-content-primary-inverted opacity-0 group-hover/commentable:opacity-100"
            aria-label={`Add comment on line ${startLine}`}
        >
            +
        </button>
    );
};

type MarkdownPreviewProps = {
    content: string;
    className?: string;
    comments?: ReviewComment[];
    onAddComment?: (
        content: string,
        startLine: number,
        endLine: number,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
};

type PendingComment = {
    startLine: number;
    endLine: number;
};

/**
 * Mutable bag of values that CommentableBlock reads at render time.
 * Stored in a ref so the ReactMarkdown `components` object stays
 * referentially stable across parent re-renders (e.g. 5s refetch).
 */
type CommentContext = {
    comments: ReviewComment[];
    pendingComment: PendingComment | null;
    enableAddComment: boolean;
    onStartComment: (startLine: number, endLine: number) => void;
    onSubmitComment: (content: string) => void;
    onCancelComment: () => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
};

/** Returns comments whose line range overlaps [startLine, endLine] */
const getCommentsForRange = (comments: ReviewComment[], startLine: number, endLine: number): ReviewComment[] => {
    return comments.filter((c) => c.startLine <= endLine && c.endLine >= startLine);
};

/**
 * Wraps a block-level markdown element to support inline comments.
 * Reads mutable state from a ref so it can live inside a stable `components` object.
 */
const CommentableBlock: FC<{
    node: Element | undefined;
    children: ReactNode;
    className?: string;
    ctxRef: React.RefObject<CommentContext>;
}> = ({ node, children, className, ctxRef }) => {
    const isNested = useContext(CommentableNestingContext);

    // Nested blocks just render children — only the outermost block
    // shows the "+" button and comment annotations.
    if (isNested) {
        return <div className={className}>{children}</div>;
    }

    const ctx = ctxRef.current!;
    const startLine = node?.position?.start?.line ?? 0;
    const endLine = node?.position?.end?.line ?? 0;

    return (
        <CommentableNestingContext.Provider value={true}>
            <CommentableBlockInner
                startLine={startLine}
                endLine={endLine}
                className={className}
                ctxRef={ctxRef}
                ctx={ctx}
            >
                {children}
            </CommentableBlockInner>
        </CommentableNestingContext.Provider>
    );
};

/** Inner component so hooks (useMemo, useCallback) aren't called conditionally */
const CommentableBlockInner: FC<{
    startLine: number;
    endLine: number;
    children: ReactNode;
    className?: string;
    ctxRef: React.RefObject<CommentContext>;
    ctx: CommentContext;
}> = ({ startLine, endLine, children, className, ctxRef, ctx }) => {
    const blockComments = useMemo(
        () => (startLine > 0 ? getCommentsForRange(ctx.comments, startLine, endLine) : []),
        [ctx.comments, startLine, endLine],
    );

    const isPendingHere =
        ctx.pendingComment !== null &&
        ctx.pendingComment.startLine === startLine &&
        ctx.pendingComment.endLine === endLine;

    const handleAddClick = useCallback(() => {
        if (startLine > 0) {
            ctxRef.current!.onStartComment(startLine, endLine);
        }
    }, [startLine, endLine, ctxRef]);

    const hasAnnotations = blockComments.length > 0 || isPendingHere;

    return (
        <>
            <div
                className={cn("group/commentable relative", className)}
                data-source-start={startLine || undefined}
                data-source-end={endLine || undefined}
            >
                {ctx.enableAddComment && startLine > 0 && (
                    <CommentGutterButton onPress={handleAddClick} startLine={startLine} />
                )}
                {children}
            </div>
            {hasAnnotations && (
                <div className="my-1">
                    {blockComments.map((comment) => (
                        <InlineCommentAnnotation
                            key={comment.id}
                            startLine={comment.startLine}
                            endLine={comment.endLine}
                            defaultComment={comment.content}
                            defaultEditable={false}
                            isOutdated={false}
                            author={comment.author}
                            commentId={comment.id}
                            filePath={comment.filePath}
                            thumbsUp={comment.thumbsUp}
                            thumbsDown={comment.thumbsDown}
                            onSubmit={(newContent) => ctx.onUpdateComment?.(comment, newContent)}
                            onCancel={ctx.onCancelComment}
                            onDelete={() => ctx.onDeleteComment?.(comment)}
                            onThumbsUp={
                                ctx.onSetCommentThumbsUp ? (up) => ctx.onSetCommentThumbsUp!(comment, up) : undefined
                            }
                            onThumbsDown={
                                ctx.onSetCommentThumbsDown
                                    ? (down) => ctx.onSetCommentThumbsDown!(comment, down)
                                    : undefined
                            }
                        />
                    ))}
                    {isPendingHere && (
                        <InlineCommentAnnotation
                            startLine={ctx.pendingComment!.startLine}
                            endLine={ctx.pendingComment!.endLine}
                            defaultComment=""
                            defaultEditable={true}
                            onSubmit={ctx.onSubmitComment}
                            onCancel={ctx.onCancelComment}
                        />
                    )}
                </div>
            )}
        </>
    );
};

const MarkdownPreviewBase: FC<MarkdownPreviewProps> = ({
    content,
    className,
    comments = [],
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onSetCommentThumbsUp,
    onSetCommentThumbsDown,
}) => {
    const [pendingComment, setPendingComment] = useState<PendingComment | null>(null);

    const handleStartComment = useCallback((startLine: number, endLine: number) => {
        setPendingComment({ startLine, endLine });
    }, []);

    const handleSubmitComment = useCallback(
        (commentContent: string) => {
            if (pendingComment && onAddComment) {
                onAddComment(commentContent, pendingComment.startLine, pendingComment.endLine, "additions");
            }
            setPendingComment(null);
        },
        [pendingComment, onAddComment],
    );

    const handleCancelComment = useCallback(() => {
        setPendingComment(null);
    }, []);

    const enableAddComment = !!onAddComment;
    const hasCommentSupport = enableAddComment || comments.length > 0;

    // Keep a ref that always points to the latest comment state and callbacks.
    // The `components` object (below) is created once and reads from this ref,
    // so ReactMarkdown never sees a new `components` identity on re-render
    // (which would cause full unmount/remount of the markdown tree).
    const ctxRef = useRef<CommentContext>({
        comments,
        pendingComment,
        enableAddComment,
        onStartComment: handleStartComment,
        onSubmitComment: handleSubmitComment,
        onCancelComment: handleCancelComment,
        onUpdateComment,
        onDeleteComment,
        onSetCommentThumbsUp,
        onSetCommentThumbsDown,
    });
    ctxRef.current = {
        comments,
        pendingComment,
        enableAddComment,
        onStartComment: handleStartComment,
        onSubmitComment: handleSubmitComment,
        onCancelComment: handleCancelComment,
        onUpdateComment,
        onDeleteComment,
        onSetCommentThumbsUp,
        onSetCommentThumbsDown,
    };

    // Build the components map once per comment-support mode.
    // Each commentable wrapper closes over ctxRef (a stable ref),
    // so the object identity stays the same across re-renders.
    const components: Components = useMemo(() => {
        if (!hasCommentSupport) {
            return plainComponents;
        }

        const wrap = <P extends JSX.IntrinsicElements[keyof JSX.IntrinsicElements] & ExtraProps>(
            renderInner: (props: P) => ReactNode,
        ): ((props: P) => ReactNode) => {
            const Wrapped = (props: P) => {
                const { node } = props;
                return (
                    <CommentableBlock node={node} ctxRef={ctxRef}>
                        {renderInner(props)}
                    </CommentableBlock>
                );
            };
            Wrapped.displayName = `Commentable(${renderInner.name || "Anonymous"})`;
            return Wrapped;
        };

        return {
            h1: wrap<JSX.IntrinsicElements["h1"] & ExtraProps>(({ children, node: _node, ...props }) => (
                <h1 className="pb-2 text-xl font-medium" {...props}>
                    {children}
                </h1>
            )),
            h2: wrap<JSX.IntrinsicElements["h2"] & ExtraProps>(({ children, node: _node, ...props }) => (
                <h2 className="pb-1 text-lg font-medium" {...props}>
                    {children}
                </h2>
            )),
            h3: wrap<JSX.IntrinsicElements["h3"] & ExtraProps>(({ children, node: _node, ...props }) => (
                <h3 className="pb-2 text-md font-medium" {...props}>
                    {children}
                </h3>
            )),
            ul: plainComponents.ul,
            ol: plainComponents.ol,
            li: wrap<JSX.IntrinsicElements["li"] & ExtraProps>(({ children, node: _node, ...props }) => (
                <li className="mb-1" {...props}>
                    {children}
                </li>
            )),
            a: plainComponents.a,
            blockquote: wrap<JSX.IntrinsicElements["blockquote"] & ExtraProps>(
                ({ children, node: _node, ...props }) => (
                    <blockquote className="mb-4 border-l-4 border-border-base py-1 pl-4 text-content-muted" {...props}>
                        {children}
                    </blockquote>
                ),
            ),
            table: wrap<JSX.IntrinsicElements["table"] & ExtraProps>(({ children, node: _node, ...props }) => (
                <div className="mb-4 overflow-hidden overflow-x-auto rounded-lg border border-border-subtle [overflow-wrap:normal] [word-break:normal]">
                    <table className="w-full" {...props}>
                        {children}
                    </table>
                </div>
            )),
            thead: plainComponents.thead,
            th: plainComponents.th,
            td: plainComponents.td,
            p: wrap<JSX.IntrinsicElements["p"] & ExtraProps>(({ node, children, ...props }) => {
                type MarkdownNode = { tagName?: string };
                const childNodes = node?.children as MarkdownNode[];
                if (childNodes?.[0]?.tagName === "img" && childNodes.length === 1) {
                    return <>{children}</>;
                }
                return (
                    <p className="mb-4 last:mb-0" {...props}>
                        {children}
                    </p>
                );
            }),
            code: plainComponents.code,
            pre: wrap<JSX.IntrinsicElements["pre"] & ExtraProps>(({ children, node: _node, ...props }) => (
                <pre
                    className="mb-4 overflow-x-auto rounded-md border border-border-subtle bg-surface-muted px-2 pb-1 pt-2 font-mono text-sm last:mb-0"
                    {...props}
                >
                    {children}
                </pre>
            )),
            img: plainComponents.img,
            hr: wrap<JSX.IntrinsicElements["hr"] & ExtraProps>(() => (
                <hr className="my-6 border-t border-border-strong" />
            )),
            strong: plainComponents.strong,
            em: plainComponents.em,
        };
    }, [hasCommentSupport]);

    if (!content) return null;

    return (
        <div
            {...{ [PREVIEW_ROOT_ATTR]: "" }}
            className={cn(
                "[word-break:break-word]",
                "[overflow-wrap:anywhere]",
                hasCommentSupport && "pl-7",
                className,
            )}
        >
            <ReactMarkdown components={components} remarkPlugins={remarkPlugins}>
                {content}
            </ReactMarkdown>
        </div>
    );
};

// Stable remark plugins array — avoids ReactMarkdown re-processing on every render
const remarkPlugins = [remarkGfm];

/** Plain component renderers shared between comment and non-comment modes */
const plainComponents: Components = {
    h1: ({ children, node: _node, ...props }) => (
        <h1 className="pb-2 text-xl font-medium" {...props}>
            {children}
        </h1>
    ),
    h2: ({ children, node: _node, ...props }) => (
        <h2 className="pb-1 text-lg font-medium" {...props}>
            {children}
        </h2>
    ),
    h3: ({ children, node: _node, ...props }) => (
        <h3 className="pb-2 text-md font-medium" {...props}>
            {children}
        </h3>
    ),
    ul: ({ children, node: _node, ...props }) => (
        <ul className="mb-4 list-disc pl-8" {...props}>
            {children}
        </ul>
    ),
    ol: ({ children, node: _node, ...props }) => (
        <ol className="mb-4 list-decimal pl-8 last:mb-0" {...props}>
            {children}
        </ol>
    ),
    li: ({ children, node: _node, ...props }) => (
        <li className="mb-1" {...props}>
            {children}
        </li>
    ),
    a: ({ children, node: _node, ...props }) => (
        <a className="text-content-link hover:underline" target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </a>
    ),
    blockquote: ({ children, node: _node, ...props }) => (
        <blockquote className="mb-4 border-l-4 border-border-base py-1 pl-4 text-content-muted" {...props}>
            {children}
        </blockquote>
    ),
    table: ({ children, node: _node, ...props }) => (
        <div className="mb-4 overflow-hidden overflow-x-auto rounded-lg border border-border-subtle [overflow-wrap:normal] [word-break:normal]">
            <table className="w-full" {...props}>
                {children}
            </table>
        </div>
    ),
    thead: ({ children, node: _node, ...props }) => (
        <thead className="border-b border-border-subtle bg-surface-muted" {...props}>
            {children}
        </thead>
    ),
    th: ({ children, node: _node, ...props }) => (
        <th className="whitespace-nowrap px-4 py-2 text-left text-sm font-semibold" {...props}>
            {children}
        </th>
    ),
    td: ({ children, node: _node, ...props }) => (
        <td className="border-t border-border-subtle px-4 py-2 text-sm" {...props}>
            {children}
        </td>
    ),
    p: ({ node, children, ...props }) => {
        type MarkdownNode = { tagName?: string };
        const childNodes = node?.children as MarkdownNode[];
        if (childNodes?.[0]?.tagName === "img" && childNodes.length === 1) {
            return <>{children}</>;
        }
        return (
            <p className="mb-4 last:mb-0" {...props}>
                {children}
            </p>
        );
    },
    code: ({ children, node: _node, ...props }) => (
        <code className="rounded-sm bg-surface-muted px-0.5 font-mono text-content-muted" {...props}>
            {children}
        </code>
    ),
    pre: ({ children, node: _node, ...props }) => (
        <pre
            className="mb-4 overflow-x-auto rounded-md border border-border-subtle bg-surface-muted px-2 pb-1 pt-2 font-mono text-sm last:mb-0"
            {...props}
        >
            {children}
        </pre>
    ),
    img: ({ node: _node, ...props }) => (
        <img className="my-2 h-auto max-w-full rounded-md" {...props} alt={props.alt || ""} />
    ),
    hr: () => <hr className="my-6 border-t border-border-strong" />,
    strong: ({ children, node: _node, ...props }) => (
        <strong className="font-semibold" {...props}>
            {children}
        </strong>
    ),
    em: ({ children, node: _node, ...props }) => (
        <em className="italic" {...props}>
            {children}
        </em>
    ),
};

export const MarkdownPreview = memo(MarkdownPreviewBase, (prevProps, nextProps) => {
    return (
        prevProps.content === nextProps.content &&
        prevProps.className === nextProps.className &&
        prevProps.comments === nextProps.comments &&
        prevProps.onAddComment === nextProps.onAddComment &&
        prevProps.onUpdateComment === nextProps.onUpdateComment &&
        prevProps.onDeleteComment === nextProps.onDeleteComment &&
        prevProps.onSetCommentThumbsUp === nextProps.onSetCommentThumbsUp &&
        prevProps.onSetCommentThumbsDown === nextProps.onSetCommentThumbsDown
    );
});
